import { Component, ElementRef, EventEmitter, forwardRef, HostBinding, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * A modern token/chip input for building up a string[] value — type a value,
 * press Enter/comma to commit it as a chip, click a chip's × (or Backspace on
 * an empty input) to remove it. Drop-in `[(ngModel)]="myArray"` usage.
 */
@Component({
    selector: 'app-chip-tokens',
    templateUrl: './chip-tokens.component.html',
    styleUrls: ['./chip-tokens.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipTokensComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class ChipTokensComponent implements ControlValueAccessor {
    @Input() placeholder = '';
    @Input() inputId?: string;

    @HostBinding('class.is-invalid') @Input() invalid = false;

    @Input() set disabled(value: boolean) {
        this.isDisabled = value;
    }

    @Output() chipAdded = new EventEmitter<string>();
    @Output() chipRemoved = new EventEmitter<string>();

    @ViewChild('chipInput', { static: true }) chipInput!: ElementRef<HTMLInputElement>;

    @HostBinding('class.is-focused') focused = false;
    @HostBinding('class.is-disabled') isDisabled = false;

    value: string[] = [];

    private onChange: (value: string[]) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(val: unknown): void {
        this.value = Array.isArray(val) ? [...val] : [];
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    focusInput(): void {
        if (this.isDisabled) {
            return;
        }
        this.chipInput.nativeElement.focus();
    }

    onFocus(): void {
        this.focused = true;
    }

    onBlur(): void {
        this.focused = false;
        this.onTouched();
        this.commitFromInput();
    }

    onKeydown(event: KeyboardEvent): void {
        if (this.isDisabled) {
            return;
        }
        const input = this.chipInput.nativeElement;

        if (event.key === 'Backspace' && !input.value && this.value.length) {
            this.removeAt(this.value.length - 1);
            return;
        }

        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            this.commitFromInput();
        }
    }

    onPaste(event: ClipboardEvent): void {
        if (this.isDisabled) {
            return;
        }
        const text = event.clipboardData?.getData('text') ?? '';
        if (!text.includes(',') && !text.includes('\n')) {
            return;
        }
        event.preventDefault();
        this.addTokens(text.split(/[,\n]+/));
        this.clearInput();
    }

    removeAt(index: number): void {
        if (this.isDisabled || index < 0 || index >= this.value.length) {
            return;
        }
        const removed = this.value[index];
        this.value = this.value.filter((_, i) => i !== index);
        this.propagateChange();
        this.chipRemoved.emit(removed);
        this.focusInput();
    }

    private commitFromInput(): void {
        this.addTokens([this.chipInput.nativeElement.value]);
        this.clearInput();
    }

    private addTokens(tokens: string[]): void {
        let added = false;
        for (const token of tokens) {
            const trimmed = token.trim();
            if (!trimmed || this.value.includes(trimmed)) {
                continue;
            }
            this.value = [...this.value, trimmed];
            this.chipAdded.emit(trimmed);
            added = true;
        }
        if (added) {
            this.propagateChange();
        }
    }

    private propagateChange(): void {
        this.onChange([...this.value]);
    }

    private clearInput(): void {
        this.chipInput.nativeElement.value = '';
    }
}
